import { jwtDecode } from 'jwt-decode';
import type { Dispatch } from '@reduxjs/toolkit';
import { config } from '../../../config/config';
import { setUserToken } from '../../../redux/slices/appInfo/slice';
import { store as persistentStore } from '../../../store/Store';
import { removeSignedInHint } from './signedInHintStore';
import { loginAsAdminOfToken, secondsUntilExpiry } from '../loginAs/loginAsClaim';
import { endExpiredLoginAsSession } from '../loginAs/loginAsSession';

/**
 * Refresh a session this many seconds before its token expires. A session
 * token lives one hour (MUSIC24_JWT_TTL_SECONDS in fym-common).
 */
export const REFRESH_BEFORE_EXPIRY_SECONDS = 5 * 60;

/** Seconds until the token should be refreshed. 0 or less means now. Null when the token has no `exp`. */
export function secondsUntilRefresh(token: string, nowMs: number = Date.now()): number | null {
  let exp: unknown;
  try {
    ({ exp } = jwtDecode<{ exp?: number }>(token));
  } catch {
    return null;
  }
  if (typeof exp !== 'number') {
    return null;
  }
  return exp - REFRESH_BEFORE_EXPIRY_SECONDS - Math.floor(nowMs / 1000);
}

export type RefreshOutcome = { kind: 'refreshed'; token: string } | { kind: 'signedOut' } | { kind: 'failed' };

export interface RefreshOptions {
  /** The API answered 401 for a call with this token. A normal session is refreshed either way. */
  unauthorized?: boolean;
}

let inFlight: { token: string; promise: Promise<RefreshOutcome> } | null = null;

/** The admin session tokens whose session ended here: each one ends its session one time. */
const endedLoginAsTokens = new Set<string>();

async function requestRefresh(token: string, dispatch: Dispatch): Promise<RefreshOutcome> {
  let response: Response;
  try {
    response = await window.fetch(`${config.fymApiURL(true)}authorization/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ token }),
    });
  } catch (error) {
    console.error('Session refresh failed', error);
    return { kind: 'failed' };
  }
  if (response.status === 401) {
    // The session is over: expired too long ago, or the account is gone.
    dispatch(setUserToken({ token: null }));
    persistentStore.remove('userToken').catch(console.error);
    removeSignedInHint();
    return { kind: 'signedOut' };
  }
  if (!response.ok) {
    return { kind: 'failed' };
  }
  const body = (await response.json()) as { token?: unknown };
  if (typeof body.token !== 'string') {
    return { kind: 'failed' };
  }
  await persistentStore.set('userToken', body.token);
  dispatch(setUserToken({ token: body.token }));
  return { kind: 'refreshed', token: body.token };
}

/**
 * Exchanges the session token for a new one (`POST /authorization/refresh`).
 * The new token carries the capabilities of the plan the account has now.
 *
 * Calls for the same token share one request. A 401 signs the user out; a
 * network error leaves the session alone so the caller can try again.
 *
 * An admin session (`loginAsAdmin` in the token) sends no request: the API refuses to refresh it.
 * It ends when its token expires. It also ends when the caller says that the API refused the token
 * (`unauthorized: true`, after a 401): the user ended all sessions, or the admin session was
 * ended. No new token can come, so each later call would fail until the token expires. One token
 * ends its session one time; a later call for the same token changes nothing.
 */
export function refreshSessionToken(
  token: string,
  dispatch: Dispatch,
  options: RefreshOptions = {}
): Promise<RefreshOutcome> {
  if (loginAsAdminOfToken(token) !== null) {
    if (endedLoginAsTokens.has(token)) {
      return Promise.resolve({ kind: 'signedOut' });
    }
    const secondsLeft = secondsUntilExpiry(token);
    if (options.unauthorized === true || (secondsLeft !== null && secondsLeft <= 0)) {
      endedLoginAsTokens.add(token);
      endExpiredLoginAsSession(dispatch);
      return Promise.resolve({ kind: 'signedOut' });
    }
    return Promise.resolve({ kind: 'failed' });
  }
  if (inFlight?.token === token) {
    return inFlight.promise;
  }
  const promise = requestRefresh(token, dispatch).finally(() => {
    if (inFlight?.promise === promise) {
      inFlight = null;
    }
  });
  inFlight = { token, promise };
  return promise;
}

/**
 * Ends every session of the user on the server (`POST /authorization/logout`).
 * Fire and forget: `keepalive` lets the request finish while the page reloads.
 * A failure is only logged; the local logout goes on either way.
 */
export function revokeSessionOnServer(token: string): void {
  window
    .fetch(`${config.fymApiURL(true)}authorization/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ token }),
      keepalive: true,
    })
    .catch((error: unknown) => console.error('Session logout failed', error));
}
